import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Paperclip, Send, X } from "lucide-react";
import api from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/format";
import { fileToBase64 } from "@/lib/pdf";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const MAX = 15 * 1024 * 1024;

export const ComposeDialog = ({ open, initial, onClose, onSent }) => {
  const [f, setF] = useState({ to: "", cc: "", subject: "", text: "" });
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  useEffect(() => {
    if (open) { setF({ to: "", cc: "", subject: "", text: "", in_reply_to: "", references: "", ...initial }); setFiles([]); }
  }, [open, initial]);

  const addFiles = (e) => {
    const next = [...files, ...Array.from(e.target.files || [])];
    if (next.reduce((s, x) => s + x.size, 0) > MAX) toast.error("Allegati troppo grandi (max 15 MB in totale)");
    else setFiles(next);
    if (fileRef.current) fileRef.current.value = "";
  };

  const send = async () => {
    if (!f.to.trim() || !f.subject.trim()) { toast.error("Inserisci destinatario e oggetto"); return; }
    setSending(true);
    try {
      const attachments = await Promise.all(files.map(async (x) => ({ filename: x.name, mime: x.type || "application/octet-stream", content_b64: await fileToBase64(x) })));
      await api.post("/mail/send", { ...f, attachments });
      toast.success(`Email inviata a ${f.to}`);
      onSent?.(); onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="mail-compose-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">{f.in_reply_to ? "Rispondi" : "Nuova email"}</DialogTitle>
          <DialogDescription>L'email parte dalla casella collegata e ne viene salvata una copia nella posta inviata.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs">A</Label><Input value={f.to} onChange={set("to")} placeholder="cliente@email.it" data-testid="mail-compose-to" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Cc</Label><Input value={f.cc || ""} onChange={set("cc")} data-testid="mail-compose-cc" /></div>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Oggetto</Label><Input value={f.subject} onChange={set("subject")} data-testid="mail-compose-subject" /></div>
          <Textarea value={f.text} onChange={set("text")} rows={10} className="font-sans" data-testid="mail-compose-body" />
          <div className="flex gap-2 flex-wrap items-center">
            <input ref={fileRef} type="file" multiple className="hidden" onChange={addFiles} data-testid="mail-compose-file-input" />
            <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="mail-compose-attach"><Paperclip className="w-4 h-4 mr-1.5" /> Allega</Button>
            {files.map((x, i) => (
              <span key={i} className="text-xs rounded-full bg-secondary px-2.5 py-1 flex items-center gap-1">
                {x.name}<button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))}><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={send} disabled={sending} data-testid="mail-compose-send">{sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1.5" /> Invia</>}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ComposeDialog;
